import _ from 'lodash';

export const DataFilter = {
    /**
     * Normalizes string values by escaping special characters and wrapping the string in quotes (")
     * so that multi-word strings are recognized correctly. Does not modify other types (numbers, etc.).
     * For example, `my" val` is normalized to `"my \" val"`
     */
    _normalize: (value) => JSON.stringify(value),

    /**
     * Performs an equality (`x == y`) comparison between the given attribute and value.
     * If this is a string comparison, casing is taken into account.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any|any[]} value The desired value of the attribute.
     * If an array is provided, the filter will match if the attribute matches _any_
     * of the values in the array.
     */
    equal: (attribute, value) => {
        if (_.isArray(value)) {
            return DataFilter.in(attribute, value);
        }

        const normalized = DataFilter._normalize(value);
        return `${attribute} EQ ${normalized}`;
    },

    /**
     * Performs an inequality (`x != y`) comparison between the given attribute and value.
     * If this is a string comparison, casing is taken into account.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any} value The desired value of the attribute.
     */
    notEqual: (attribute, value) => `${attribute} NEQ ${DataFilter._normalize(value)}`,

    /**
     * Performs a greater-than (`x > y`) comparison between the given attribute and value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The desired value of the attribute.
     */
    greaterThan: (attribute, value) => `${attribute} GT ${DataFilter._normalize(value)}`,

    /**
     * Performs a less-than (`x < y`) comparison between the given attribute and value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The desired value of the attribute.
     */
    lessThan: (attribute, value) => `${attribute} LT ${DataFilter._normalize(value)}`,

    /**
     * Performs a greater-than-or-equal-to (`x >= y`) comparison between the given attribute and value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The desired value of the attribute.
     */
    greaterThanOrEqual: (attribute, value) => `${attribute} GEQ ${DataFilter._normalize(value)}`,

    /**
     * Performs a less-than-or-equal-to (`x <= y`) comparison between the given attribute and value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The desired value of the attribute.
     */
    lessThanOrEqual: (attribute, value) => `${attribute} LEQ ${DataFilter._normalize(value)}`,

    /**
     * Creates a filter that checks whether the value of the attribute matches any of the given values.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any[]} values The desired values of the attribute.
     */
    in: (attribute, values) => {
        const normalizedValues = _.castArray(values);
        return `${attribute} IN ${DataFilter._normalize(normalizedValues)}`;
    },

    /**
     * Creates a filter that checks whether the value of the attribute does not match any of the given values.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any[]} values The desired values of the attribute.
     */
    notIn: (attribute, values) => {
        const normalizedValues = _.castArray(values);
        return `${attribute} NIN ${DataFilter._normalize(normalizedValues)}`;
    },

    /**
     * Creates a filter that checks whether the value of the attribute is between the given values.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} min The minimum value of the attribute, inclusive.
     * @param {number} max The maximum value of the attribute, exclusive.
     */
    between: (attribute, min, max) => {
        const parameters = [min, max];
        return `${attribute} BETWEEN ${DataFilter._normalize(parameters)}`;
    },

    /**
     * Performs a case-sensitive string comparison between the given attribute and value.
     * Checks if the attribute contains the given value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any} value The desired value of the attribute.
     */
    contains: (attribute, value) => `${attribute} STRCON ${DataFilter._normalize(value)}`,

    /**
     * Performs a case-sensitive string comparison between the given attribute and value.
     * Checks if the attribute starts with the given value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any} value The desired value of the attribute.
     */
    startsWith: (attribute, value) => `${attribute} STRSTART ${DataFilter._normalize(value)}`,

    /**
     * Performs a case-insensitive string comparison (`x == y`) between the given attribute and value.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {any} value
     */
    equalIgnoreCase: (attribute, value) => `${attribute} STREQ ${DataFilter._normalize(value)}`,

    /**
     * Performs a bitwise AND (`x & y != 0`) comparison between the given attribute and value.
     * Currently, this filter is limited to checking whether the bitwise and operation
     * between the `attribute` and `value` is non-zero.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The value to use in the comparison.
     */
    bitwiseAnd: (attribute, value) => `${attribute} & ${DataFilter._normalize(value)}`,

    /**
     * Performs a bitwise OR (`x | y != 0`) comparison between the given attribute and value.
     * Currently, this filter is limited to checking whether the bitwise or operation
     * between the `attribute` and `value` is non-zero.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The value to use in the comparison.
     */
    bitwiseOr: (attribute, value) => `${attribute} | ${DataFilter._normalize(value)}`,

    /**
     * Performs a bitwise XOR (`x ^ y != 0`) comparison between the given attribute and value.
     * Currently, this filter is limited to checking whether the bitwise xor operation
     * between the `attribute` and `value` is non-zero.
     * @param {string} attribute The name of the attribute to filter on.
     * @param {number} value The value to use in the comparison.
     */
    bitwiseXor: (attribute, value) => `${attribute} ^ ${DataFilter._normalize(value)}`,

    /**
     * Combines multiple filters using the `OR` operator creating a new filter
     * that will match if at least one of the underlying filters identifies a match.
     * @param {string[]} filters
     */
    or: (filters) => `(${filters.filter(_.negate(_.isEmpty)).join(' OR ')})`,

    /**
     * Combines multiple filters using the `AND` operator creating a new filter
     * that will match if all of the combined filters identify a match.
     * @param {string[]} filters
     */
    and: (filters) => filters.filter(_.negate(_.isEmpty)).join(' AND '),

    /**
     * Alias for `DataFilter.equal`.
     * @see DataFilter.equal
     */
    get eq() {
        return this.equal;
    },

    /**
     * Alias for `DataFilter.notEqual`.
     * @see DataFilter.notEqual
     */
    get neq() {
        return this.notEqual;
    },

    /**
     * Alias for `DataFilter.greaterThan`.
     * @see DataFilter.greaterThan
     */
    get gt() {
        return this.greaterThan;
    },

    /**
     * Alias for `DataFilter.lessThan`.
     * @see DataFilter.lessThan
     */
    get lt() {
        return this.lessThan;
    },

    /**
     * Alias for `DataFilter.greaterThanOrEqual`.
     * @see DataFilter.greaterThanOrEqual
     */
    get geq() {
        return this.greaterThanOrEqual;
    },

    /**
     * Alias for `DataFilter.lessThanOrEqual`.
     * @see DataFilter.lessThanOrEqual
     */
    get leq() {
        return this.lessThanOrEqual;
    },

    /**
     * Alias for `DataFilter.notIn`.
     * @see DataFilter.notIn
     */
    get nin() {
        return this.notIn;
    },

    /**
     * Alias for `DataFilter.contains`.
     * @see DataFilter.contains
     */
    get strcon() {
        return this.contains;
    },

    /**
     * Alias for `DataFilter.startsWith`.
     * @see DataFilter.startsWith
     */
    get strstart() {
        return this.startsWith;
    },

    /**
     * Alias for `DataFilter.equalIgnoreCase`.
     * @see DataFilter.equalIgnoreCase
     */
    get streq() {
        return this.equalIgnoreCase;
    },

    /**
     * Alias for `DataFilter.bitwiseAnd`.
     * @see DataFilter.bitwiseAnd
     */
    get band() {
        return this.bitwiseAnd;
    },

    /**
     * Alias for `DataFilter.bitwiseOr`.
     * @see DataFilter.bitwiseOr
     */
    get bor() {
        return this.bitwiseOr;
    },

    /**
     * Alias for `DataFilter.bitwiseXor`.
     * @see DataFilter.bitwiseXor
     */
    get bxor() {
        return this.bitwiseXor;
    },
};
